<template>
  <ApplicationLayout>
    <v-container
      fluid
      px-12
      py-6
      v-if="ready"
    >
      <BackTitle
        :to="{
          name: $route.query.from || 'home',
          params: { id: $route.query.id },
          query: { focus: product.id }
        }"
        :title="title"
        :subtitle="productTitle"
      ></BackTitle>

      <v-card
        class="pa-6 mt-6"
      >
        <v-row>
          <v-col
            cols="6"
          >
            <v-form
              @submit.prevent="submit()"
              ref="form"
              v-model="valid"
            >
              <v-select
                :disabled="product.status === 'disabled'"
                v-model="product.status"
                :items="statuses"
                label="Status"
              ></v-select>

              <Combobox
                v-if="product.flux_id"
                v-model="product.tags"
                :select-props="{
                  items: _tagsFiltered,
                  label: 'Additional tags',
                  chips: true,
                  deletableChips: true,
                  multiple: true
                }"
                @create-new-item="$store.dispatch('Tags/createAndAdd', { name: $event, tags: product.tags })"
              ></Combobox>

              <Combobox
                v-model="product.reseller_id"
                :select-props="{
                  items: _resellers,
                  label: 'Reseller',
                  clearable: true
                }"
                @create-new-item="$store.dispatch('Resellers/createAndSet', { name: $event, item: product })"
              ></Combobox>

              <h4
                class="headline my-6"
                v-if="product.flux_id"
              >Data from feed</h4>

              <v-text-field
                :disabled="locked"
                v-model="product.title"
                label="Title"
                :rules="rules.title"
              ></v-text-field>

              <v-textarea
                auto-grow
                :disabled="locked"
                v-model="product.description"
                label="Description"
              ></v-textarea>

              <v-select
                :disabled="locked"
                v-if="product.flux_id"
                v-model="product.flux_tags"
                :items="_tags"
                chips
                label="Tags"
                multiple
              ></v-select>

              <Combobox
                v-if="!product.flux_id"
                v-model="product.tags"
                :select-props="{
                  items: _tags,
                  label: 'Tags',
                  chips: true,
                  deletableChips: true,
                  multiple: true
                }"
                @create-new-item="$store.dispatch('Tags/createAndAdd', { name: $event, tags: product.tags })"
              ></Combobox>

              <v-text-field
                :disabled="locked"
                v-model="product.url"
                label="URL"
                :rules="rules.url"
              ></v-text-field>

              <TextFieldImage
                label="Image"
                v-model="product.image"
                name="productImage"
                :disabled="locked"
                hideUploadWhenDisabled
              ></TextFieldImage>

              <v-text-field
                :disabled="locked"
                v-model="product.price"
                label="Price"
                type="number"
              ></v-text-field>

              <Combobox
                v-model="product.brand_id"
                :select-props="{
                  disabled: locked,
                  items: _brands,
                  label: 'Brand'
                }"
                @create-new-item="$store.dispatch('Brands/createAndSet', { name: $event, item: product })"
              ></Combobox>

              <v-select
                :disabled="locked"
                v-model="product.gender"
                :items="genders"
                label="Gender"
              ></v-select>

              <div
                class="
                  mt-6
                  d-flex
                  justify-end
                "
              >
                <v-btn
                  :disabled="!modified"
                  v-if="editMode"
                  class="mr-4"
                  @click="cancel()"
                  depressed
                >Cancel</v-btn>

                <v-btn
                  :disabled="!valid || !modified"
                  class="mr-4"
                  color="primary"
                  type="submit"
                  depressed
                >Save</v-btn>

                <v-btn
                  :disabled="!valid || !modified"
                  color="primary"
                  @click="saveAndGoBack()"
                  depressed
                >Save & Go back</v-btn>
              </div>
            </v-form>
          </v-col>

          <v-col
            cols="6"
          >
            <div
              class="
                d-flex
                fill-height
                align-center
                justify-center
              "
            >
              <img
                v-if="product.image"
                :src="product.image | decodeUri"
                :alt="'image - ' + product.title"
                style="max-width: 70%"
              />
            </div>
          </v-col>
        </v-row>
      </v-card>
    </v-container>
  </ApplicationLayout>
</template>

<script>
import { mapState } from 'vuex'
import ApplicationLayout from '../components/layouts/ApplicationLayout.vue'
import Combobox from '../components/Combobox.vue'
import BackTitle from '../components/BackTitle.vue'
import TextFieldImage from '../components/TextFieldImage.vue'

export default {
  components: {
    ApplicationLayout,
    Combobox,
    BackTitle,
    TextFieldImage
  },
  data() {
    return {
      valid: false,
      ready: false,
      locked: true,
      editMode: !!this.$route.params.id,
      productCopy: null,
      rules: {
        title: [v => !!v || 'Title is required'],
        url: [
          v => !!v || 'URL is required',
          v => /https?:\/\/.+/.test(v) || 'URL must be valid'
        ]
      },
      genders: [
        { value: 'male', text: this.$translate.instant('GENDER_MALE') },
        { value: 'female', text: this.$translate.instant('GENDER_FEMALE') },
        { value: 'neutral', text: this.$translate.instant('GENDER_NEUTRAL') }
      ]
    }
  },
  computed: {
    ...mapState('Products', ['product', 'productTitle']),
    ...mapState('Resellers', ['resellers']),
    ...mapState('Tags', ['tags']),
    ...mapState('Brands', ['brands']),
    _resellers() {
      return this.resellers.map(reseller => ({
        text: reseller.name,
        value: reseller.id
      }))
    },
    _tags() {
      return this.tags.map(tag => ({
        text: tag.name,
        value: tag.id
      }))
    },
    _tagsFiltered() {
      return this.tags
        .filter(tag => !this.product.flux_tags.includes(tag.id))
        .map(tag => ({
          text: tag.name,
          value: tag.id
        }))
    },
    _brands() {
      return this.brands.map(brand => ({
        text: brand.name,
        value: brand.id
      }))
    },
    title() {
      return this.editMode ? 'Product' : 'Create new product'
    },
    modified() {
      return this.productCopy !== JSON.stringify(this.product)
    },
    statuses() {
      if (this.product.status === 'disabled') {
        return [{
          value: 'disabled',
          text: this.$translate.instant('PRODUCT_DISABLED')
        }]
      }
      return [
        {
          value: 'enabled',
          text: this.$translate.instant('PRODUCT_ENABLED')
        },
        {
          value: 'manually_disabled',
          text: this.$translate.instant('PRODUCT_MANUALLY_DISABLED')
        }
      ]
    }
  },
  async created() {
    await this.getProduct(this.$route.params.id)
    this.productCopy = JSON.stringify(this.product)
    this.$store.dispatch('Resellers/getAll')
    this.$store.dispatch('Tags/getAll')
    this.$store.dispatch('Brands/getAll')
    this.ready = true
  },
  methods: {
    async getProduct(id) {
      await this.$store.dispatch('Products/get', id)
      this.locked = this.editMode && !!this.product.flux_id
    },
    async submit() {
      if (!this.$refs.form.validate()) return
      const snackbar = { text: 'Product update succeed' }
      try {
        await this.$store.dispatch('Products/createOrUpdate')
        if (!this.editMode) {
          this.$router.push({ name: 'product', params: { id: this.product.id } })
          snackbar.text = 'Product creation succeed'
          this.editMode = true
        }
        this.productCopy = JSON.stringify(this.product)
      } catch (error) {
        snackbar.text = this.editMode ? 'Product update failed' : 'Product creation failed'
        snackbar.color = 'error'
      }
      this.$store.commit('Global/setSnackbar', snackbar)
    },
    cancel() {
      this.$store.commit('Products/setProduct', JSON.parse(this.productCopy))
    },
    async saveAndGoBack() {
      await this.submit()
      this.$router.push({ name: 'home' })
    }
  },
  async beforeRouteUpdate(to, from, next) {
    if (to.name === from.name && to.params.id !== from.params.id) {
      await this.getProduct(to.params.id)
    }
    next()
  },
  beforeRouteLeave(to, from, next) {
    if (from.name !== 'new-product') {
      this.$store.commit('Products/setProduct', null)
    }
    next()
  }
}
</script>
